/**
 * Sentry Configuration
 *
 * Initializes Sentry for error tracking and performance monitoring
 */

import * as Sentry from "@sentry/react";
import { env } from "./env";

/**
 * Initialize Sentry
 * Should be called as early as possible in the application lifecycle
 */
export function initializeSentry() {
	// Only initialize if DSN is provided
	if (!env.SENTRY_DSN) {
		if (env.isDevelopment) {
			console.warn(
				"⚠️  Sentry DSN not provided. Sentry will not be initialized.",
			);
		}
		return;
	}

	Sentry.init({
		dsn: env.SENTRY_DSN,
		environment: env.SENTRY_ENVIRONMENT,

		// Performance Monitoring
		integrations: [
			// Browser tracing for performance monitoring
			Sentry.browserTracingIntegration(),
			// Replay integration for session recording
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
			}),
		],

		// Set sample rate for performance monitoring
		// 1.0 = 100% of transactions are sent
		// Lower this in production to reduce costs
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/.*ramps\.moneygram\.com/,
		],

		// Performance Monitoring: Sample rate for performance monitoring
		// Adjust based on your needs and budget
		tracesSampleRate: env.isProduction ? 0.1 : 1.0, // 10% in production, 100% in development

		// Session Replay: Sample rate for session replay
		// This determines how many sessions will be recorded
		replaysSessionSampleRate: env.isProduction ? 0.1 : 0.5, // 10% in production, 50% in dev

		// Session Replay: Sample rate when an error occurs
		// This records sessions when errors happen
		replaysOnErrorSampleRate: 1.0, // Always record when errors occur

		// Before sending events, you can modify them
		beforeSend(event, hint) {
			// Filter out certain errors if needed
			if (event.exception) {
				const error = hint.originalException;

				// Example: Don't send network errors in development
				if (
					env.isDevelopment &&
					error instanceof Error &&
					error.message.includes("Network")
				) {
					return null;
				}
			}

			return event;
		},

		// Enable debug mode in development
		debug: env.isDevelopment,

		// Release tracking (useful for source maps)
		release: import.meta.env.VITE_APP_VERSION || "development",

		// Ignore certain errors
		ignoreErrors: [
			// Browser extensions
			"top.GLOBALS",
			// Random plugins/extensions
			"originalCreateNotification",
			"canvas.contentDocument",
			"MyApp_RemoveAllHighlights",
			// Network errors that are expected
			"Non-Error promise rejection captured",
		],

		// Deny URLs (don't track errors from these URLs)
		denyUrls: [
			// Browser extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^chrome-extension:\/\//i,
		],
	});

	if (env.isDevelopment) {
		console.log("✅ Sentry initialized successfully");
	}
}

// Export Sentry for manual error tracking if needed
export { Sentry };
